import { Card, Table } from '@chakra-ui/react';
import { SubscriptionView } from '@monetize/types/app';
import { useMemo } from 'react';
import { MBox, MCard, MFlex, MText } from '../Monetize';
import { MSpinner } from '../Monetize/chakra';
import { SubscriptionOfferingSection } from './SubscriptionOfferingSection';
import { TableColumnHeader } from './SubscriptionViewTable';

interface SubscriptionViewCardProps {
  data: SubscriptionView | undefined;
  isLoading: boolean;
  isError: boolean;
  title?: string;
}

export const SubscriptionViewCard = ({
  data,
  isLoading,
  isError,
  title = 'Products',
}: SubscriptionViewCardProps) => {
  const sortedOfferings = useMemo(
    () =>
      data?.offerings
        ? [...data.offerings].sort((a, b) => a.position - b.position)
        : [],
    [data?.offerings],
  );

  const showLoading = isLoading;
  const showError = !isLoading && isError;
  const showEmpty = !isLoading && !isError && sortedOfferings.length === 0;
  const showOfferings =
    !isLoading && !isError && !!data && sortedOfferings.length > 0;

  return (
    <MCard variant="border" borderRadius="md">
      <Card.Header pt={4} pb={2} px={4}>
        <MText fontWeight="bold" fontSize="lg" color="tPurple.dark">
          {title}
        </MText>
      </Card.Header>
      <Card.Body pt={0} px={0}>
        {showLoading && (
          <MFlex py={8} justifyContent="center">
            <MSpinner size="md" color="tIndigo.base" />
          </MFlex>
        )}
        {showError && (
          <MFlex py={8} justifyContent="center">
            <MText color="tGray.darkPurple">Unable to load products</MText>
          </MFlex>
        )}
        {showEmpty && (
          <MFlex py={8} justifyContent="center">
            <MText color="tGray.darkPurple">No active products</MText>
          </MFlex>
        )}
        {showOfferings && (
          <MBox overflowX="auto">
            <Table.Root size="sm" variant="noHover">
              <Table.Header>
                <Table.Row>
                  <TableColumnHeader>Product</TableColumnHeader>
                  <TableColumnHeader textAlign="end">
                    Quantity
                  </TableColumnHeader>
                  <TableColumnHeader textAlign="end">
                    Unit Price
                  </TableColumnHeader>
                </Table.Row>
              </Table.Header>
              {sortedOfferings.map((offering) => (
                <SubscriptionOfferingSection
                  key={offering.activeSegment.quoteOfferingId}
                  offering={offering}
                  currency={data.currency}
                />
              ))}
            </Table.Root>
          </MBox>
        )}
      </Card.Body>
    </MCard>
  );
};
